// LinkVMModal.jsx - Modal for linking bots to Proxmox VMs
// Supports fuzzy matching and manual selection

import { useState, useEffect, useMemo } from 'react'
import { X, Server, Search, Check, AlertTriangle, Link2, Unlink } from 'lucide-react'
import { monitoringApi } from '../services/api'
import './LinkVMModal.css'

export default function LinkVMModal({ bot, onClose, onLinked }) {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [vms, setVMs] = useState([])
    const [suggestion, setSuggestion] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedVM, setSelectedVM] = useState(null)
    const [saving, setSaving] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)

    // Fetch VMs and suggestion on mount
    useEffect(() => {
        async function fetchData() {
            if (!bot?.server_ip) {
                setError('No server IP available for this bot')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                const res = await monitoringApi.vmSuggest(
                    bot.server_ip,
                    bot.sip_id,
                    bot.dialer_name || ''
                )

                if (res.data.success) {
                    setVMs(res.data.vms || [])
                    setSuggestion(res.data.suggestion)

                    // If auto-confirm, pre-select
                    if (res.data.suggestion?.autoConfirm) {
                        setSelectedVM(res.data.suggestion)
                    }
                } else {
                    setError(res.data.error || 'Failed to fetch VMs')
                }
            } catch (err) {
                setError(err.response?.data?.error || err.message)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [bot])

    // Filter VMs based on search
    const filteredVMs = useMemo(() => {
        if (!searchTerm) return vms
        const term = searchTerm.toLowerCase()
        return vms.filter(vm =>
            vm.name?.toLowerCase().includes(term) ||
            String(vm.vmid).includes(term)
        )
    }, [vms, searchTerm])

    // Handle link
    const handleLink = async () => {
        if (!selectedVM) return

        try {
            setSaving(true)
            // v3.98: Pass dialer_id to prevent cross-dialer linking issues
            await monitoringApi.linkBotToVM(bot.sip_id, selectedVM.vmid, bot.server_ip, bot.dialer_id)
            onLinked?.(selectedVM)
            onClose()
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to link VM')
            setSaving(false)
        }
    }

    // Handle unlink
    const handleUnlink = async () => {
        try {
            setSaving(true)
            await monitoringApi.unlinkBotFromVM(bot.sip_id, bot.dialer_id)
            onLinked?.(null)
            onClose()
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to unlink VM')
            setSaving(false)
        }
    }

    // Format match score as percentage
    const formatScore = (score) => Math.round(score * 100) + '%'

    return (
        <div className="link-vm-overlay" onClick={onClose}>
            <div className="link-vm-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="link-vm-header">
                    <div className="link-vm-title">
                        <Server size={18} />
                        <span>Link VM to Bot {bot?.sip_id}</span>
                    </div>
                    <button className="link-vm-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="link-vm-content">
                    {loading ? (
                        <div className="link-vm-loading">
                            <div className="link-vm-spinner" />
                            <span>Fetching VMs from {bot?.server_ip}...</span>
                        </div>
                    ) : error ? (
                        <div className="link-vm-error">
                            <AlertTriangle size={20} />
                            <span>{error}</span>
                        </div>
                    ) : (
                        <>
                            {/* Current Link Status */}
                            {bot?.vmid && (
                                <div className="link-vm-current">
                                    <span className="link-vm-current-label">Currently linked to:</span>
                                    <span className="link-vm-current-value">
                                        VM {bot.vmid}
                                        {vms.find(v => v.vmid === bot.vmid)?.name &&
                                            ` (${vms.find(v => v.vmid === bot.vmid).name})`
                                        }
                                    </span>
                                    <button
                                        className="link-vm-unlink-btn"
                                        onClick={handleUnlink}
                                        disabled={saving}
                                    >
                                        <Unlink size={12} /> Unlink
                                    </button>
                                </div>
                            )}

                            {/* Suggestion Card */}
                            {suggestion && !selectedVM && (
                                <div className={`link-vm-suggestion ${suggestion.autoConfirm ? 'auto' : 'needs-confirm'}`}>
                                    <div className="link-vm-suggestion-header">
                                        {suggestion.autoConfirm ? (
                                            <Check size={16} className="success" />
                                        ) : (
                                            <AlertTriangle size={16} className="warning" />
                                        )}
                                        <span>
                                            {suggestion.autoConfirm
                                                ? 'Exact Match Found!'
                                                : 'Possible Match Found'}
                                        </span>
                                        <span className="link-vm-score">{formatScore(suggestion.matchScore)} match</span>
                                    </div>
                                    <div className="link-vm-suggestion-body">
                                        <div className="link-vm-vm-info">
                                            <span className="link-vm-vm-name">{suggestion.name}</span>
                                            <span className="link-vm-vm-id">VM ID: {suggestion.vmid}</span>
                                            <span className={`link-vm-vm-status ${suggestion.status}`}>
                                                {suggestion.status}
                                            </span>
                                        </div>
                                        <div className="link-vm-match-reason">
                                            Matched: {suggestion.matchedPattern}
                                        </div>
                                    </div>
                                    <div className="link-vm-suggestion-actions">
                                        <button
                                            className="link-vm-btn link-vm-btn-confirm"
                                            onClick={() => setSelectedVM(suggestion)}
                                        >
                                            <Check size={14} />
                                            {suggestion.autoConfirm ? 'Use This VM' : 'Confirm Match'}
                                        </button>
                                        <button
                                            className="link-vm-btn link-vm-btn-reject"
                                            onClick={() => {
                                                setSuggestion(null)
                                                setShowDropdown(true)
                                            }}
                                        >
                                            Select Different
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Selected VM Display */}
                            {selectedVM && (
                                <div className="link-vm-selected">
                                    <div className="link-vm-selected-header">
                                        <Check size={16} className="success" />
                                        <span>Selected VM</span>
                                    </div>
                                    <div className="link-vm-vm-info large">
                                        <span className="link-vm-vm-name">{selectedVM.name}</span>
                                        <span className="link-vm-vm-id">VM ID: {selectedVM.vmid}</span>
                                        <span className={`link-vm-vm-status ${selectedVM.status}`}>
                                            {selectedVM.status}
                                        </span>
                                    </div>
                                    <button
                                        className="link-vm-change-btn"
                                        onClick={() => {
                                            setSelectedVM(null)
                                            setShowDropdown(true)
                                        }}
                                    >
                                        Change Selection
                                    </button>
                                </div>
                            )}

                            {/* Manual Selection */}
                            {(!suggestion || showDropdown) && !selectedVM && (
                                <div className="link-vm-manual">
                                    <div className="link-vm-manual-header">
                                        <Search size={14} />
                                        <span>Select VM Manually</span>
                                    </div>
                                    <div className="link-vm-search">
                                        <Search size={14} />
                                        <input
                                            type="text"
                                            placeholder="Search by name or VM ID..."
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="link-vm-list">
                                        {filteredVMs.length === 0 ? (
                                            <div className="link-vm-no-results">
                                                No VMs found {searchTerm && `matching "${searchTerm}"`}
                                            </div>
                                        ) : (
                                            filteredVMs.map(vm => (
                                                <button
                                                    key={vm.vmid}
                                                    className={`link-vm-list-item ${bot?.vmid === vm.vmid ? 'current' : ''}`}
                                                    onClick={() => setSelectedVM(vm)}
                                                >
                                                    <span className="link-vm-item-name">{vm.name || `VM ${vm.vmid}`}</span>
                                                    <span className="link-vm-item-id">{vm.vmid}</span>
                                                    <span className={`link-vm-item-status ${vm.status}`}>
                                                        {vm.status}
                                                    </span>
                                                    {bot?.vmid === vm.vmid && (
                                                        <span className="link-vm-item-current">current</span>
                                                    )}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="link-vm-footer">
                    <button className="link-vm-btn link-vm-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="link-vm-btn link-vm-btn-save"
                        onClick={handleLink}
                        disabled={!selectedVM || saving}
                    >
                        {saving ? 'Linking...' : (
                            <>
                                <Link2 size={14} /> Link VM
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}
